import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { build, stack } from '../../../../../common/classes/request-builder';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { EntidadesApiService } from '../../../../../service/api/entidades-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TransaccionesApiService } from '../../../../../service/api/transacciones-api.service';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../../view/base-form';
import { environment } from '../../../../../../environments/environment';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { DropdownData, FilteredValue } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import { FormType } from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from 'src/app/common/components/reactive-form-builder/interfaces/reactive-form-change.interface';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { EntidadModel } from 'src/app/models/reto/entidad.model';
import { TransaccionModel } from 'src/app/models/reto/transaccion.model';
import { TransaccionesFormDefinition } from './form-control/form-definition';
import { RequestHandler } from 'src/app/service/OffService/request-handler';

@Component({
    selector: 'app-transacciones-form',
    standalone: false,
    templateUrl: './transacciones-form.component.html',
    styleUrls: ['./transacciones-form.component.scss'],
})
export class TransaccionesFormComponent extends BaseForm<TransaccionModel> implements OnInit, OnDestroy {
    public form!: TransaccionesFormDefinition;

    public appName = environment.appName;

    public hasClients = environment.features.hasClients;

    public fincaName = environment.features.fincaName;

    public fincasName = environment.features.fincasName;

    public showReto = environment.features.showReto;

    public override model = { fecha: '' } as TransaccionModel;

    public error = '';

    public getRequest!: RequestHandler<TransaccionModel[]>;

    private products: ProductosModel[] = [];

    private entities: EntidadModel[] = [];

    public constructor(
        public override router: Router,
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public transaccionesApi: TransaccionesApiService,
        private readonly productosApi: ProductosApiService,
        public entidadesApi: EntidadesApiService,
        public clientesApi: ClientesApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            transaccionesApi.TransaccionesPUT,
            transaccionesApi.TransaccionesPOST,
            'transacciones',
            'Editar transacción',
            'Crear transacción',
            'Duplicar transacción',
        );

        this.getRequest = this.getType() === FormType.DUPLICATION
            ? this.transaccionesApi.TransaccionesGET_GENERIC
            : this.transaccionesApi.TransaccionesGET;
    }// constructor();

    public async ngOnInit(): Promise<void> {
        this.form = new TransaccionesFormDefinition(this.getType(), this.model);

        await this.initFormData();
        this.softInit(this.getType());
    }// ngOnInit();

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }// ngOnDestroy();

    public reactiveFormChanges(
        formModel: ReactiveFormValue<TransaccionModel>,
        lastChangedFieldName: ReactiveFormChangedField<TransaccionModel>,
    ): void {
        this.form.updateModel(formModel, lastChangedFieldName);
        this.model = this.form.model as TransaccionModel;
        this.formChanges(String(lastChangedFieldName ?? ''));
    }// reactiveFormChanges();

    public formChanges(tag: string): void {
        let shouldRefreshDropdowns = false;

        if (tag === 'descuadre' && this.form.model.descuadre === true) {
            this.form.model.almacen_origen = '';
            this.form.model.almacen_destino = '';
        }// if();

        if ((tag === 'almacen_origen' || tag === 'almacen_destino') && this.hasWarehouseNumber(tag)) {
            this.form.model.descuadre = false;
        }// if();

        if (tag === 'id_entidad_origen') {
            this.form.model.ropo_origen = '';
            this.fillRopoOrigen();
            shouldRefreshDropdowns = true;
        }// if();

        if (tag === 'id_entidad_destino') {
            this.form.model.ropo_destino = '';
            this.fillRopoDestino();
            shouldRefreshDropdowns = true;
        }// if();

        if (tag === 'tipo_transaccion') {
            this.prepareDestinationForTransactionType(true);
            shouldRefreshDropdowns = true;
        }// if();

        if (tag === '__previous_data_patch' || tag === '__dropdown_refresh') {
            this.prepareDestinationForTransactionType(false);
            shouldRefreshDropdowns = tag === '__previous_data_patch';
        }// if();

        if (
            tag === 'id_entidad_origen' ||
            tag === 'ropo_origen' ||
            tag === 'tipo_transaccion' ||
            tag === 'id_entidad_destino' ||
            tag === 'ropo_destino' ||
            tag === '__previous_data_patch' ||
            tag === '__dropdown_refresh'
        ) {
            this.syncConditionalSections();
        }// if();

        if (tag === 'num_envases' || tag === 'capacidad_envase') {
            this.form.calculateTotalVolume();
        }// if();

        if (tag === 'id_producto' || tag === '__previous_data_patch' || tag === '__dropdown_refresh') {
            this.form.syncSelectedProductUnits(this.products);
        }// if();

        this.model = this.form.model as TransaccionModel;
        this.form.refreshSections();
        this.formBuilderRef?.updateForm(this.form.model as Record<string, unknown>, false);

        if (shouldRefreshDropdowns) {
            this.dropdownLoadTrigger.next();
        }// if();
    }// formChanges();

    public override goBack(): void {
        this.request.reset();
        this.router.navigate(['dashboard', 'reto-transacciones']);
    }// goBack();

    private async initFormData(): Promise<void> {
        try {
            const [products, entities] = await Promise.all([
                this.productosApi.productos.GET.toPromise(),
                this.entidadesApi.EntidadesGET.toPromise(),
                stack([build(this.clientesApi.clientesGET, this.form.lists.clientes, 'nombre')]),
            ]);

            this.products = (products ?? []) as ProductosModel[];
            this.entities = (entities ?? []) as EntidadModel[];

            this.fillProducts();
            this.fillEntities();
            this.prepareDestinationForTransactionType(false);
        } catch (error) {
            this.error = 'No se han podido cargar los datos auxiliares de transacciones.';
            console.error(this.error, error);
        } finally {
            this.form.refreshSections();
            this.formBuilderRef?.updateForm(this.form.model as Record<string, unknown>, false);
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();
        }// try();
    }// initFormData();

    private fillProducts(): void {
        this.resetDropdown(this.form.lists.productos);

        this.products
            .filter((product) => product.tipo === 'fitosanitario')
            .forEach((product) => {
                this.addDropdownOption(this.form.lists.productos, product.nombre ?? '', product.id ?? '', {
                    unidades: product.unidades?.toString() ?? '',
                });
            });
    }// fillProducts();

    private fillEntities(): void {
        this.resetDropdown(this.form.lists.entidadesOrigen);
        this.resetDropdown(this.form.lists.entidadesDestino);

        this.entities
            .filter((entity) => this.canUseEntity(entity))
            .forEach((entity) => {
                this.addDropdownOption(this.form.lists.entidadesOrigen, entity.nombre ?? '', entity.id ?? '');
                this.addDropdownOption(this.form.lists.entidadesDestino, entity.nombre ?? '', entity.id ?? '');
            });
    }// fillEntities();

    private fillRopoOrigen(): void {
        this.resetDropdown(this.form.lists.ropoOrigen);
        const selectedEntity = this.findEntityById(this.form.model.id_entidad_origen);

        if (selectedEntity !== undefined) {
            this.fillEntityRopoList(this.form.lists.ropoOrigen, selectedEntity);
        }// if();

        this.selectSingleRopoOption(this.form.lists.ropoOrigen, 'ropo_origen');
    }// fillRopoOrigen();

    private fillRopoDestino(): void {
        this.resetDropdown(this.form.lists.ropoDestino);

        if (String(this.form.model.tipo_transaccion ?? '') === '8' && environment.features.hasClients) {
            this.fillClientRopoDestino();
        } else {
            const selectedEntity = this.findEntityById(this.form.model.id_entidad_destino);

            if (selectedEntity !== undefined) {
                this.fillEntityRopoList(this.form.lists.ropoDestino, selectedEntity);
            }// if();
        }// if();

        this.selectSingleRopoOption(this.form.lists.ropoDestino, 'ropo_destino');
    }// fillRopoDestino();

    private prepareDestinationForTransactionType(shouldClearDestinationRopo: boolean): void {
        if (shouldClearDestinationRopo) {
            this.form.model.ropo_destino = '';
        }// if();

        if (String(this.form.model.tipo_transaccion ?? '') !== '8') {
            delete this.form.model.cultivo_tratamiento;
            this.fillEntities();
        }// if();

        this.fillRopoOrigen();
        this.fillRopoDestino();
        this.form.refreshSections();
    }// prepareDestinationForTransactionType();

    private syncConditionalSections(): void {
        if (!this.form.isWarehouseTransfer()) {
            this.form.deleteWarehouseData();
        }// if();

        this.form.refreshSections();
    }// syncConditionalSections();

    private hasWarehouseNumber(tag: string): boolean {
        const value = this.form.model[tag as keyof TransaccionModel];

        return value !== undefined && value !== null && String(value) !== '';
    }// hasWarehouseNumber();

    private canUseEntity(entity: EntidadModel): boolean {
        const role = StorageManager.getUser().rol ?? '';
        const assignedUserId = StorageManager.getUser().tecnico_asignado ?? '';

        return role === 'admin' || assignedUserId === entity.id_usuario;
    }// canUseEntity();

    private findEntityById(entityId: unknown): EntidadModel | undefined {
        const normalizedId = String(entityId ?? '');

        return this.entities.find((entity) => String(entity.id ?? '') === normalizedId);
    }// findEntityById();

    private fillEntityRopoList(dropdown: DropdownData<string | null>, entity: EntidadModel): void {
        [entity.roposs, entity.ropost, entity.roposu]
            .filter((ropo): ropo is string => ropo != null && ropo.trim().length > 1)
            .forEach((ropo) => this.addDropdownOption(dropdown, ropo, ropo));
    }// fillEntityRopoList();

    private fillClientRopoDestino(): void {
        const selectedDestinationId = String(this.form.model.id_entidad_destino ?? '');
        const selectedClient = this.form.lists.clientes.filtered
            .map((clientOption) => clientOption.value)
            .find((client) => String(client?.id ?? '') === selectedDestinationId);

        if (selectedClient?.ropo == null || selectedClient.ropo.trim() === '') {
            return;
        }// if();

        this.addDropdownOption(this.form.lists.ropoDestino, selectedClient.ropo, selectedClient.ropo);
        this.form.lists.ropoDestino.selected = selectedClient.ropo;
    }// fillClientRopoDestino();

    private selectSingleRopoOption(
        dropdown: DropdownData<string | null>,
        modelField: 'ropo_origen' | 'ropo_destino',
    ): void {
        if (dropdown.filtered.length === 2) {
            this.form.model[modelField] = dropdown.filtered[1]?.value ?? '';
        }// if();
    }// selectSingleRopoOption();

    private resetDropdown<T>(dropdown: DropdownData<T>): void {
        const placeholder: FilteredValue<T> = { label: '...', value: null };

        dropdown.selected = null;
        dropdown.filtered = [placeholder];
        dropdown.values = [placeholder];
    }// resetDropdown();

    private addDropdownOption<T>(
        dropdown: DropdownData<T>,
        label: string,
        value: T | null,
        extra: Partial<FilteredValue<T>> = {},
    ): void {
        const option: FilteredValue<T> = {
            label,
            value,
            ...extra,
        };

        dropdown.filtered.push(option);
        dropdown.values.push(option);
    }// addDropdownOption();
}// class;
